import { useQuery } from "@tanstack/react-query";

import type { DriverCurrentRoutes } from "@storyboard/web-client";
import { getMembershipCurrentRoutes, routeKeys } from "@storyboard/web-client";
import { transport } from "@/config";
import { idx, rowsByIndex } from "@/store/store";
import { useCollectionsCompute } from "@/store/useCollectionsCompute";
import type { DriverRouteSummary } from "@/types/driverRoute";
import { summarizeDriverRoutes, summarizeWorkspaceRoutes } from "./driverRouteSummary";
import { useRefreshFailureEvent } from "./useRefreshFailureEvent";

/** How often an open Context tab re-reads its one driver: one indexed row, cheap enough to keep the tiles current. */
export const MEMBERSHIP_ROUTES_REFRESH_MS = 30_000;

/** Stable answer with no workspace, so rows do not re-project on a fresh empty map every render. */
const NO_ROUTES: ReadonlyMap<string, DriverRouteSummary> = new Map();

// Module scope so Query memoizes the select across renders; an inline arrow would re-summarize every render.
const selectMembershipRoutes = (data: DriverCurrentRoutes) => summarizeDriverRoutes(data.routes);

/**
 * Every driver's current route in a workspace, by membership id, as the store holds them.
 *
 * The store is kept current by the workspace's current-routes sync (`models/currentRoutes`), which survives a reload,
 * so a returning board renders its rows before any request completes.
 */
export function useCurrentRoutes(workspaceId: string | undefined): ReadonlyMap<string, DriverRouteSummary> {
  return useCollectionsCompute(
    ["currentRoute"],
    () =>
      workspaceId
        ? summarizeWorkspaceRoutes(rowsByIndex("currentRoute", idx.currentRoutesByWorkspace, workspaceId))
        : NO_ROUTES,
    [workspaceId],
    "Dispatch:currentRoutes",
  );
}

/**
 * One driver's current route, polled from the per-membership read for as long as the caller stays mounted.
 *
 * `undefined` until the first answer lands, so a caller can fall back to the row's own summary; `null` once the read
 * says the driver is on nothing.
 */
export function useMembershipCurrentRoutes(
  workspaceId: string | undefined,
  membershipId: string | undefined,
): DriverRouteSummary | null | undefined {
  const query = useQuery({
    queryKey: routeKeys.membershipCurrent(workspaceId ?? "", membershipId ?? ""),
    queryFn: () =>
      getMembershipCurrentRoutes(transport, { workspaceId: workspaceId ?? "", membershipId: membershipId ?? "" }),
    enabled: Boolean(workspaceId) && Boolean(membershipId),
    refetchInterval: MEMBERSHIP_ROUTES_REFRESH_MS,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: false,
    select: selectMembershipRoutes,
  });
  useRefreshFailureEvent(query, "current_routes.membership_refresh_failed", "currentRoutes", {
    workspaceId,
    membershipId,
  });
  return query.data;
}
